<script setup lang="ts">
import './assets/style.scss'
import { ListFieldList } from '@/entities/Inventory/TypeField'
import { VIcons } from '@/shared/ui'
import { type PropType, ref, computed } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { useModel } from './../model/model.ts'

const props = defineProps({
  field: {
    type: Object as PropType<ObjectInventoryField>,
    required: true,
  },
  searchQuery: {
    type: String,
    default: '',
  },
})

const emits = defineEmits(['drag'])
const open = ref(false)

const { loadData, fieldList, inventoryType } = useModel()

const { isFetching } = loadData(props.field?.object_type || 0, open)

const normalizedSearchQuery = computed(() => props.searchQuery.trim().toLowerCase())

const filteredFieldList = computed(() => {
  if (!normalizedSearchQuery.value) return fieldList.value

  return fieldList.value.filter((item) =>
    (item.name || '').toLowerCase().includes(normalizedSearchQuery.value),
  )
})

const hasMatchingChildren = computed(
  () => normalizedSearchQuery.value && filteredFieldList.value.length > 0,
)

const isVisible = computed(() => {
  if (!normalizedSearchQuery.value) return true

  const selfMatch = (props.field?.name || '')
    .toLowerCase()
    .includes(normalizedSearchQuery.value)

  return selfMatch || hasMatchingChildren.value
})

/**
 * Функция проброса события перетаскивания
 */
const dragHandler = (field: ObjectInventoryField) => {
  emits('drag', {
    name: field.name,
    id: field.id,
    inventory_type: inventoryType.value ?? undefined,
  })
}

const clickHandler = () => {
  if (!props.field) return

  open.value = !open.value
}
</script>

<template>
  <div v-if="isVisible" class="invent-pass-grid-field__item">
    <div class="invent-pass-grid-field__main">
      <VTextField
        class="invent-pass-grid-field__field"
        v-model="field.name"
        readonly
        hide-details
        :draggable="true"
        @drag="dragHandler(field)"
      >
      </VTextField>

      <VBtn
        color="default"
        variant="tonal"
        v-if="field.type === ListFieldList.LIST_TYPES"
        :loading="isFetching"
        @click="clickHandler"
      >
        <VIcons
          class="invent-pass-grid-field__icon"
          :class="{ 'invent-pass-grid-field__icon_open': open }"
          type="keyboard_arrow_down"
        />
      </VBtn>
    </div>

    <div v-if="open && !isFetching" class="invent-pass-grid-field__children">
      <template v-if="fieldList.length">
        <InventTypePassportGridField
          @drag="dragHandler(item)"
          v-for="item in filteredFieldList"
          :field="item"
          :search-query="searchQuery"
        />
      </template>

      <div v-else class="invent-pass-grid-field__children_empty">Нет полей для сетки</div>
    </div>
  </div>
</template>
